import { fork, take, put, select, call, takeLatest } from 'redux-saga/effects';
import * as qs from 'qs';
import {
  setLoadedPlayers,
  findAutopastePlayer,
  findStats,
  setLoadedBaseStats,
  setLoadedGraphStats,
  showLoadingGraphSpinner,
  showGraphError,
  changePlayernameToFind,
  changeDateFilter,
  changeLimitFilter,
  changeTableSizeFilter,
  dropFiltersToSettingsState,
} from './actions';

import {
  selectLimitFilterValue,
  selectDateFilterValue,
  selectPlayerToShowStats,
  selectTableSizeFilterValue,
  selectCustomDateFilterValue,
} from './selectors';
import { getPokersiteNameByPokersiteId, prepareLoadedPlayers } from './utils';
import type { PlayerDto } from '@poker-stats/contracts';
import type { PlayerStatsResponseDto as PlayerStatsDto } from '@poker-stats/contracts';
import { history } from '../../main';
import type { PlayerGraphResultsDto } from '@poker-stats/contracts';
import apiService from '../../api/apiServices/apiService';

import { toast } from 'react-toastify';
import { TableSizeOptions } from '../../components/TableSizeFilter/constants';
import { LimitOptions } from '../../components/LimitFilter/constants';
import { DateOptions } from '../../components/DateFilter/constants';
import { statReducer } from './reducer';
import { PokersiteOptions } from '../../components/PokersiteFilter/constants';
import i18n from '../../../i18n';
import { showFilterSearchError } from './notifications';

function syncStatsUrl(playername: string, pokersiteId: 1 | 2 | 3 | 4 | 5 | 6) {
  const room = getPokersiteNameByPokersiteId(pokersiteId);
  const statsPath = '/statistics';
  const currentParams = qs.parse(history.location.search, { ignoreQueryPrefix: true });

  if (
    history.location.pathname === statsPath &&
    currentParams.playername === playername &&
    currentParams.room === room
  ) {
    return;
  }

  const querystring = qs.stringify({ playername, room });
  history.push(`${statsPath}?${querystring}`);
}

function* findGraphResultsWorker(
  playername: string,
  pokersiteId: 1 | 2 | 3 | 4 | 5 | 6,
  filters: ReturnType<typeof generateFilters>,
) {
  yield put(showLoadingGraphSpinner());

  const { success, reject } = yield call(
    [apiService, apiService.getPlayerGraph],
    playername,
    pokersiteId,
    filters,
  );

  if (success) {
    yield put(setLoadedGraphStats(success.payload as PlayerGraphResultsDto));
  }

  if (reject) {
    yield put(showGraphError());
  }
}
function* findStatsWorker(
  playername: string,
  pokersiteId: 1 | 2 | 3 | 4 | 5 | 6,
  filters: ReturnType<typeof generateFilters>,
) {
  const { success, reject } = yield call(
    [apiService, apiService.getPlayerStats],
    playername,
    pokersiteId,
    filters,
  );

  if (success) {
    const payload = success.payload as PlayerStatsDto;
    yield put(setLoadedBaseStats(payload));

    syncStatsUrl(playername, pokersiteId);
  }

  if (reject) {
    if (reject.payload.subcode === 2) {
      yield put(statReducer.actions.showMiningError(pokersiteId));
    }
    if (reject.payload.subcode === 3) {
      yield put(
        setLoadedBaseStats({
          playername,
          pokersiteId,
          sameNickPlayers: [],
          limitStats: [],
          tableSizeStats: [],
          monthStats: [],
          generalStats: {},
        }),
      );
      yield put(
        setLoadedGraphStats({
          result: [],
          resultWithShowdown: [],
          resultWithoutShowdown: [],
          resultEv: [],
        }),
      );

      syncStatsUrl(playername, pokersiteId);

      yield call(showFilterSearchError, i18n.t('filter_search_error_message'));
      return false;
    }
    toast.error(reject.payload.message);
  }

  return true;
}

function* watchFindStat({ payload }: ReturnType<typeof findStats>) {
  // while (true) {
  // const { payload }: ReturnType<typeof findStats> =
  //   yield takeLatest(findStats);

  const { playername, pokersiteId } = payload;

  const limitFilter: ReturnType<typeof selectLimitFilterValue> =
    yield select(selectLimitFilterValue);
  const tableSizeFilter: ReturnType<typeof selectTableSizeFilterValue> = yield select(
    selectTableSizeFilterValue,
  );

  const dateFilter: ReturnType<typeof selectDateFilterValue> = yield select(selectDateFilterValue);

  const customDateFilter: ReturnType<typeof selectCustomDateFilterValue> = yield select(
    selectCustomDateFilterValue,
  );

  const filters = generateFilters(tableSizeFilter, limitFilter, dateFilter, customDateFilter);

  const shouldLoadGraph: boolean = yield call(findStatsWorker, playername, pokersiteId, filters);
  if (shouldLoadGraph) {
    yield call(findGraphResultsWorker, playername, pokersiteId, filters);
  }
  // }
}

function* watchChangePlayernameToFind() {
  while (true) {
    const { payload }: ReturnType<typeof changePlayernameToFind> = yield take(
      changePlayernameToFind.type,
    );
    const playername = payload;

    if (playername === '') {
      continue;
    }

    const { success, reject } = yield call([apiService, apiService.getPlayers], playername);

    if (success) {
      const players = prepareLoadedPlayers(success.payload as Array<PlayerDto>);
      yield put(setLoadedPlayers(players));
    }

    if (reject) {
      yield put(setLoadedPlayers([]));
    }
  }
}

function* watchFindAutopastePlayer({
  payload: playername,
}: ReturnType<typeof findAutopastePlayer>) {
  const { success, reject } = yield call([apiService, apiService.getAutopastePlayer], playername);

  if (success) {
    const player = success.payload as PlayerDto;

    yield put(dropFiltersToSettingsState());
    yield put(
      findStats({
        playername: player.playername,
        pokersiteId: player.pokersite_id,
      }),
    );
  }

  if (reject) {
    toast.error(reject.payload.message);
  }
}

function* watchChangeDateToFind() {
  while (true) {
    yield take(changeDateFilter.type);

    const { playername, pokersiteId }: ReturnType<typeof selectPlayerToShowStats> =
      yield select(selectPlayerToShowStats);

    yield put(
      findStats({
        playername,
        pokersiteId,
      }),
    );
  }
}

function* watchChangeLimitToFind() {
  while (true) {
    yield take(changeLimitFilter.type);

    const { playername, pokersiteId }: ReturnType<typeof selectPlayerToShowStats> =
      yield select(selectPlayerToShowStats);

    yield put(
      findStats({
        playername,
        pokersiteId,
      }),
    );
  }
}

function* watchTableSizeToFind() {
  while (true) {
    yield take(changeTableSizeFilter.type);

    const { playername, pokersiteId }: ReturnType<typeof selectPlayerToShowStats> =
      yield select(selectPlayerToShowStats);

    yield put(
      findStats({
        playername,
        pokersiteId,
      }),
    );
  }
}

export function generateFilters(
  tableSizeFilterValue?: number, //id фильтра
  limitFilterValue?: number, //id фильтра
  dateFilterValue?: number, //id фильтра
  customDateFilterValue?: { fromDate: string; toDate: string },
  pokersiteFilterValue?: number,
) {
  let fromDateFilter: string = undefined!;
  let toDateFilter: string = undefined!;
  // let fromLimitFilter: number = undefined!;
  // let toLimitFilter: number = undefined!;
  let limitFilter: number = undefined!;
  let fromTableSizeFilter: number = undefined!;
  let toTableSizeFilter: number = undefined!;

  const tableSizeOption = TableSizeOptions.find((x) => x.id === tableSizeFilterValue);

  switch (tableSizeOption) {
    case undefined:
      break;
    default:
      fromTableSizeFilter = tableSizeOption.tableSizeFrom;
      toTableSizeFilter = tableSizeOption.tableSizeTo;
  }

  const limitOption = LimitOptions.find((x) => x.id === limitFilterValue)!;

  switch (limitOption.id) {
    case 0:
      break;
    default:
      // fromLimitFilter = limitOption.limit;
      // toLimitFilter = limitOption.limit;
      limitFilter = limitOption.limit;
  }

  const dateOption = DateOptions.find((x) => x.id === dateFilterValue);

  switch (dateOption) {
    case undefined:
      break;
    default:
      fromDateFilter = dateOption.fromDate;
      toDateFilter = dateOption.toDate;
  }

  if (customDateFilterValue) {
    fromDateFilter = customDateFilterValue.fromDate;
    toDateFilter = customDateFilterValue.toDate;
  }

  return {
    fromDateFilter,
    toDateFilter,
    // fromLimitFilter,
    // toLimitFilter,
    limitFilter,
    fromTableSizeFilter,
    toTableSizeFilter,
    pokersiteFilter: pokersiteFilterValue
      ? PokersiteOptions.find((x) => x.id === pokersiteFilterValue)?.value
      : undefined,
  };
}

export default function* statSaga() {
  // yield fork(watchFindStat);
  yield takeLatest(findStats, watchFindStat);
  yield takeLatest(findAutopastePlayer, watchFindAutopastePlayer);
  yield fork(watchChangePlayernameToFind);
  yield fork(watchChangeDateToFind);
  yield fork(watchChangeLimitToFind);
  yield fork(watchTableSizeToFind);
}
